import { useTenantCurrencyConversionsQuery } from "@/actions/tenantCurrencyConversions.actions";
import { fetchMarketRate, findTenantRate } from "@/lib/currencyRates";
import { convertInventoryRates, sourceCurrenciesNeeded } from "@/lib/inventoryCurrency";
import { useAuthStore } from "@/states";
import { App } from "antd";
import { useCallback, useRef } from "react";

type InventoryKind = "hotel" | "activity" | "transport";

const MARKET_TTL_MS = 6 * 60 * 60 * 1000;
const marketCache = new Map<string, { rate: number; at: number }>();

/**
 * Converts inventory rates into the itinerary's costing currency when an item has none in it.
 * Uses the tenant's configured conversion rate for the pair (either stored direction) and falls back to the market
 * rate the itinerary page already uses, so a hotel priced only in EUR still quotes in a USD itinerary.
 * The returned items carry `rateConversions` describing what was applied; items with nothing to convert are returned as is.
 * @param currency The itinerary's costing (base) currency
 */
export const useInventoryCurrencyConverter = (currency?: string) => {
  const user = useAuthStore((state) => state.user);
  const { message } = App.useApp();
  const { data: rates = [] } = useTenantCurrencyConversionsQuery(user?.tenantId || user?.tenant?.id);
  const warned = useRef(new Set<string>());
  const target = currency?.trim().toUpperCase() || "";

  const resolveMultiplier = useCallback(
    async (from: string): Promise<number | undefined> => {
      const configured = findTenantRate(rates, from, target);
      if (configured) return configured;
      const key = `${from}/${target}`;
      const hit = marketCache.get(key);
      if (hit && Date.now() - hit.at < MARKET_TTL_MS) return hit.rate;
      try {
        const rate = await fetchMarketRate(from, target);
        marketCache.set(key, { rate, at: Date.now() });
        return rate;
      } catch {
        if (!warned.current.has(key)) {
          warned.current.add(key);
          message.warning(`Some rates are in ${from} and no ${from} to ${target} conversion rate is available. Add one in Organisation → Currency Rates.`, 8);
        }
        return undefined;
      }
    },
    [message, rates, target],
  );

  return useCallback(
    async <T,>(kind: InventoryKind, items: T[]): Promise<T[]> => {
      if (!target) return items;
      const needed = new Set(items.flatMap((item) => sourceCurrenciesNeeded(kind, item, target)));
      if (needed.size === 0) return items;
      const multipliers: Record<string, number> = {};
      await Promise.all([...needed].map(async (from) => {
        const multiplier = await resolveMultiplier(from);
        if (multiplier) multipliers[from] = multiplier;
      }));
      return items.map((item) => convertInventoryRates(kind, item, target, multipliers));
    },
    [resolveMultiplier, target],
  );
};
